import React, { useEffect, useRef } from "react";
import { pickColor, hexToRgb } from "./internal/colorUtils";

export const key = "voronoi-cells";

const DEFAULT_BG = "#0c0a1f";
const DEFAULT_CELL_A = "#7c3aed";
const DEFAULT_CELL_B = "#06b6d4";
const DEFAULT_EDGE = "#f5f3ff";

const SEED_COUNT = 14;
const SEED_SPEED = 0.06;        // px/frame at the low-res buffer scale
const LOW_W = 180;              // offscreen buffer dimensions — voronoi is
const LOW_H = 110;              //  O(seeds * pixels), so cap the resolution
const EDGE_THICKNESS = 0.045;   // 0..1 ratio of d2/d1 that counts as an edge

function rand(min, max) { return min + Math.random() * (max - min); }

// Lerp between two RGB colors (0..255 components) by k in [0..1].
function lerpRgb(a, b, k) {
  return {
    r: a.r + (b.r - a.r) * k,
    g: a.g + (b.g - a.g) * k,
    b: a.b + (b.b - a.b) * k,
  };
}

export default function VoronoiCells({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const seedsRef = useRef([]);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_CELL_A, DEFAULT_CELL_B, DEFAULT_EDGE]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_CELL_A),
      pickColor(colors, 2, DEFAULT_CELL_B),
      pickColor(colors, 3, DEFAULT_EDGE),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const off = document.createElement("canvas");
    off.width = LOW_W;
    off.height = LOW_H;
    const offCtx = off.getContext("2d");
    if (!offCtx) return undefined;

    const imageData = offCtx.createImageData(LOW_W, LOW_H);
    const pixels = imageData.data;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const seedSeeds = () => {
      const arr = new Array(SEED_COUNT);
      for (let i = 0; i < SEED_COUNT; i += 1) {
        arr[i] = {
          x: rand(0, LOW_W),
          y: rand(0, LOW_H),
          vx: rand(-SEED_SPEED, SEED_SPEED),
          vy: rand(-SEED_SPEED, SEED_SPEED),
          // Each seed leans slightly toward color A or B, giving the cells a
          // varied palette across the canvas instead of a 2-tone checkerboard.
          tint: Math.random(),
        };
      }
      seedsRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      if (!seedsRef.current.length) seedSeeds();
    };

    const tick = () => {
      const [hexBg, hexA, hexB, hexEdge] = colorsRef.current;
      const cBg = hexToRgb(hexBg) || { r: 0, g: 0, b: 0 };
      const cA = hexToRgb(hexA) || { r: 124, g: 58, b: 237 };
      const cB = hexToRgb(hexB) || { r: 6, g: 182, b: 212 };
      const cE = hexToRgb(hexEdge) || { r: 245, g: 243, b: 255 };

      // Update seed positions; bounce off bounds so coverage stays even.
      const seeds = seedsRef.current;
      for (let i = 0; i < seeds.length; i += 1) {
        const s = seeds[i];
        s.x += s.vx;
        s.y += s.vy;
        if (s.x < 0 || s.x > LOW_W) s.vx *= -1;
        if (s.y < 0 || s.y > LOW_H) s.vy *= -1;
      }

      // For each low-res pixel, find nearest + second-nearest seed. The
      // nearest determines the cell color; the ratio between the two
      // distances determines whether we're near a Voronoi edge (and thus
      // should blend in the edge color).
      let p = 0;
      for (let y = 0; y < LOW_H; y += 1) {
        for (let x = 0; x < LOW_W; x += 1) {
          let d1 = Infinity;
          let d2 = Infinity;
          let nearest = 0;
          for (let i = 0; i < seeds.length; i += 1) {
            const dx = x - seeds[i].x;
            const dy = y - seeds[i].y;
            const d = dx * dx + dy * dy; // squared — fine for ranking
            if (d < d1) {
              d2 = d1;
              d1 = d;
              nearest = i;
            } else if (d < d2) {
              d2 = d;
            }
          }

          const seed = seeds[nearest];
          const cellRgb = lerpRgb(cA, cB, seed.tint);

          // Slight darkening toward the seed center → subtle "pebble" depth.
          const cellShade = lerpRgb(cBg, cellRgb, 0.5 + 0.5 * Math.min(1, d1 / 8000));

          // Edge factor: how close d1 and d2 are. Smaller difference =
          // closer to a Voronoi cell border.
          const ratio = Math.sqrt(d1) / Math.max(0.001, Math.sqrt(d2));
          const edgeT = Math.max(0, Math.min(1, (ratio - (1 - EDGE_THICKNESS * 4)) / (EDGE_THICKNESS * 4)));
          const edgeMix = edgeT * edgeT;

          const final = lerpRgb(cellShade, cE, edgeMix * 0.85);

          pixels[p] = final.r;
          pixels[p + 1] = final.g;
          pixels[p + 2] = final.b;
          pixels[p + 3] = 255;
          p += 4;
        }
      }

      offCtx.putImageData(imageData, 0, 0);
      ctx.drawImage(off, 0, 0, width, height);

      rafRef.current = requestAnimationFrame(tick);
    };

    seedSeeds();
    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
